import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn, SCROLL_PANE } from '../../lib/utils';

/**
 * True while the dialog's own request is in flight. DialogContent reads it to
 * block Escape / outside-click / outside-focus and to disable the X.
 */
const DialogPendingContext = React.createContext(false);

/**
 * Focus goes back to whatever held it when the dialog opened (DESIGN_SYSTEM §5
 * Dialogs — stacked dialogs). Radix returns focus only to a DialogTrigger, and
 * the kit's dialogs are opened from state — a row's Open button, a Delete inside
 * another dialog, Escape on a dirty form — so without this focus falls to <body>.
 * A caller that prevents the close autofocus (ConfirmDialog after a successful
 * confirm, which leaves focus where the page moved it) is left alone, and an
 * opener that is no longer in the document is not chased.
 */
export function useReturnFocus(): {
  remember: () => void;
  restore: (event: Event) => void;
} {
  const openerRef = React.useRef<HTMLElement | null>(null);
  return React.useMemo(
    () => ({
      remember: () => {
        const active = document.activeElement;
        openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
      },
      restore: (event: Event) => {
        const target = openerRef.current;
        openerRef.current = null;
        if (event.defaultPrevented || !target || !target.isConnected) return;
        event.preventDefault();
        target.focus();
      },
    }),
    [],
  );
}

type DialogProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  /**
   * Lock every close path while a save the dialog reports inline is running
   * (DESIGN_SYSTEM §6): close requests are ignored, Escape / outside pointer /
   * outside focus are prevented, the X is disabled and dimmed, and the content
   * is aria-busy. Opening, and a close the parent performs itself by changing
   * `open`, are never blocked. Pages still disable their own footer
   * Cancel / Back. Never pass it for a background refetch.
   */
  pending?: boolean;
};

const Dialog = ({ pending = false, onOpenChange, ...props }: DialogProps) => (
  <DialogPendingContext.Provider value={pending}>
    <DialogPrimitive.Root
      {...props}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange?.(next);
      }}
    />
  </DialogPendingContext.Provider>
);

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * A control that sits in the dialog's top-right corner, beside the close X —
 * for an action on the dialog itself rather than on its content (editing the
 * record it shows). Carries the X's exact treatment so the pair reads as one
 * cluster; pass an icon plus an `sr-only` label, as the X does.
 */
const DialogIconButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, type = 'button', ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(
      'rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
      className,
    )}
    {...props}
  />
));
DialogIconButton.displayName = 'DialogIconButton';

/**
 * The dialog width scale (DESIGN_SYSTEM.md §5). Width is set ONLY through
 * `size`: these classes are merged after the caller's className, so a max-w-*
 * a dialog passes is dropped by construction. Both the bare and the `sm:` form
 * are emitted because tailwind-merge only resolves a conflict within the same
 * modifier — `max-w-[480px]` alone would not drop a page's `sm:max-w-[900px]`.
 *
 * `frame` is for fixed-height shells whose controls must not move between
 * steps or sections (record workbenches, the publish wizard): no padding or
 * gap, clipped, capped at 85vh. The shell's own height (`h-[660px]`) is the
 * one sizing class a page still passes.
 */
const dialogContentVariants = cva('', {
  variants: {
    size: {
      sm: 'max-w-[480px] sm:max-w-[480px]',
      md: 'max-w-[640px] sm:max-w-[640px]',
      lg: 'max-w-[800px] sm:max-w-[800px]',
      xl: 'max-w-[900px] sm:max-w-[900px]',
      '2xl': 'max-w-[1040px] sm:max-w-[1040px]',
      map: 'max-w-[min(1280px,95vw)] sm:max-w-[min(1280px,95vw)]',
      fullscreen: 'max-w-[95vw] sm:max-w-[95vw]',
    },
    frame: {
      true: 'max-h-[85vh] gap-0 overflow-hidden p-0 sm:p-0',
      false: '',
    },
  },
  defaultVariants: { size: 'sm', frame: false },
});

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
    VariantProps<typeof dialogContentVariants> & {
    /**
     * Rendered immediately left of the close X. The kit owns the gap between
     * them so every dialog that grows one lands in the same place.
     */
    headerAction?: React.ReactNode;
  }
>(({ className, size, frame, children, headerAction, onEscapeKeyDown, onPointerDownOutside, onFocusOutside, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const pending = React.useContext(DialogPendingContext);
  const opener = useReturnFocus();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // A flex column capped at 85vh: a DialogBody child takes the
          // remaining height and scrolls, so the header and DialogActions
          // never scroll away.
          'fixed left-[50%] top-[50%] z-50 flex max-h-[85vh] w-full translate-x-[-50%] translate-y-[-50%] flex-col gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className,
          dialogContentVariants({ size, frame }),
        )}
        aria-busy={pending || undefined}
        // The page's own handler runs first (the publish wizard blocks Escape on
        // its terminal steps); the pending lock only ever adds a preventDefault.
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          if (pending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          onPointerDownOutside?.(event);
          if (pending) event.preventDefault();
        }}
        onFocusOutside={(event) => {
          onFocusOutside?.(event);
          if (pending) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => {
          opener.remember();
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          opener.restore(event);
        }}
        {...props}
      >
        {children}
        {headerAction ? (
          <div className="absolute right-10 top-4 flex items-center">{headerAction}</div>
        ) : null}
        <DialogPrimitive.Close
          disabled={pending}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-30"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * The scrolling body of a non-frame dialog. Takes the height the header and
 * DialogActions leave inside the 85vh cap and scrolls there, never the
 * overlay. SCROLL_PANE's inset keeps focus rings unclipped.
 */
const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(SCROLL_PANE, 'min-h-0 flex-1', className)} {...props} />
  ),
);
DialogBody.displayName = 'DialogBody';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  dialogContentVariants,
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogIconButton,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
